// Página PAYOUTS — cierra el ciclo de ingreso del trading. Registra los retiros
// que la prop firm paga sobre las cuentas fondeadas. El "ingreso real" es lo que
// está cobrado (status=paid); lo pedido pero no acreditado se muestra aparte.

import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TradingAccount } from '@bini-trading/shared-types';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';

interface Payout {
  id: string;
  accountId: string;
  amount: number;
  currency: string;
  status: 'requested' | 'paid' | 'rejected';
  requestedAt: string;
  paidAt: string | null;
  method: string | null;
  profitSplitPct: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  externalRef: string | null;
  notes: string | null;
  account?: { id: string; name: string; broker: string };
}

interface PayoutSummary {
  totalCobrado: number;
  totalPendiente: number;
  cantidadPagos: number;
  cantidadPendientes: number;
  promedioPago: number;
  ultimoPago: string | null;
  proximaVentanaEstimada: string | null;
  cicloDias: number;
  currency: string;
}

const STATUS_OPTIONS = [
  { value: 'requested', label: 'Pedido (esperando)' },
  { value: 'paid', label: 'Cobrado' },
  { value: 'rejected', label: 'Rechazado' },
] as const;

const METHOD_OPTIONS = [
  { value: 'rise', label: 'Rise' },
  { value: 'deel', label: 'Deel' },
  { value: 'crypto', label: 'Cripto (USDT)' },
  { value: 'wire', label: 'Transferencia' },
  { value: 'otro', label: 'Otro' },
] as const;

const STATUS_TONE: Record<string, string> = {
  paid: 'text-win-DEFAULT bg-win-DEFAULT/10 border-win-DEFAULT/30',
  requested: 'text-warn-DEFAULT bg-warn-DEFAULT/10 border-warn-DEFAULT/30',
  rejected: 'text-loss-DEFAULT bg-loss-DEFAULT/10 border-loss-DEFAULT/30',
};
const STATUS_LABEL: Record<string, string> = {
  paid: 'Cobrado',
  requested: 'Pedido',
  rejected: 'Rechazado',
};

const fmtMoney = (amount: number, currency = 'USD'): string =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);

const fmtDate = (iso: string | null): string =>
  iso ? new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso)) : '—';

const daysUntil = (iso: string | null): number | null => {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
};

export default function Payouts(): ReactElement {
  const qc = useQueryClient();
  const [accountId, setAccountId] = useState<string>('');
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Payout | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Payout | null>(null);

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<TradingAccount[]>('/accounts'),
  });

  // Solo cuentas fondeadas tienen payouts; ofrecemos ese filtro primero.
  const fundedAccounts = useMemo(
    () => (accounts ?? []).filter((a) => a.kind === 'funded'),
    [accounts],
  );

  const accountQS = accountId ? `?accountId=${accountId}` : '';
  const { data: payouts, isLoading } = useQuery({
    queryKey: ['payouts', accountId],
    queryFn: () => api<Payout[]>(`/payouts${accountQS}`),
  });
  const { data: summary } = useQuery({
    queryKey: ['payouts-summary', accountId],
    queryFn: () => api<PayoutSummary>(`/payouts/summary${accountQS}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/payouts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts'] });
      qc.invalidateQueries({ queryKey: ['payouts-summary'] });
      setDeleteTarget(null);
    },
  });

  const markPaid = useMutation({
    mutationFn: (p: Payout) =>
      api(`/payouts/${p.id}`, {
        method: 'PATCH',
        body: { status: 'paid', paidAt: new Date().toISOString() },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts'] });
      qc.invalidateQueries({ queryKey: ['payouts-summary'] });
    },
  });

  const accountOptions = [
    { value: '', label: 'Todas las cuentas' },
    ...(accounts ?? []).map((a) => ({ value: a.id, label: a.name })),
  ];

  const proxDias = daysUntil(summary?.proximaVentanaEstimada ?? null);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs text-ink-subtle tracking-[0.18em] uppercase mb-2">
              Ingreso del trading
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Payouts</h1>
            <p className="text-ink-muted mt-2">
              La ganancia recién es ingreso cuando se cobra. Acá registrás cada retiro de tus cuentas fondeadas.
            </p>
          </div>
          <div className="flex items-end gap-3">
            <div className="w-52">
              <Select
                label="Cuenta"
                options={accountOptions}
                value={accountId}
                onChange={setAccountId}
              />
            </div>
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              + Registrar payout
            </Button>
          </div>
        </div>

        {/* Tarjetas de resumen */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Cobrado (real)"
            value={fmtMoney(summary?.totalCobrado ?? 0, summary?.currency)}
            hint={`${summary?.cantidadPagos ?? 0} pago(s)`}
            tone="win"
          />
          <StatCard
            label="Pendiente"
            value={fmtMoney(summary?.totalPendiente ?? 0, summary?.currency)}
            hint={`${summary?.cantidadPendientes ?? 0} pedido(s)`}
            tone="warn"
          />
          <StatCard
            label="Promedio por pago"
            value={fmtMoney(summary?.promedioPago ?? 0, summary?.currency)}
            hint={summary?.ultimoPago ? `Último ${fmtDate(summary.ultimoPago)}` : 'Sin pagos aún'}
          />
          <StatCard
            label="Próxima ventana"
            value={
              summary?.proximaVentanaEstimada
                ? proxDias != null && proxDias > 0
                  ? `en ${proxDias}d`
                  : 'disponible'
                : '—'
            }
            hint={
              summary?.proximaVentanaEstimada
                ? fmtDate(summary.proximaVentanaEstimada)
                : `ciclo ${summary?.cicloDias ?? 14}d`
            }
            tone={proxDias != null && proxDias <= 0 ? 'win' : undefined}
          />
        </div>

        {isLoading ? (
          <div className="text-ink-subtle text-sm font-mono py-12 text-center">Cargando…</div>
        ) : !payouts || payouts.length === 0 ? (
          <EmptyState
            onCreate={() => setShowCreate(true)}
            hasFunded={fundedAccounts.length > 0}
          />
        ) : (
          <div className="rounded-xl border border-surface-border bg-surface-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-subtle text-[11px] uppercase tracking-wider border-b border-surface-border">
                    <th className="px-4 py-3 font-semibold">Monto</th>
                    <th className="px-4 py-3 font-semibold">Estado</th>
                    <th className="px-4 py-3 font-semibold">Cuenta</th>
                    <th className="px-4 py-3 font-semibold">Pedido</th>
                    <th className="px-4 py-3 font-semibold">Cobrado</th>
                    <th className="px-4 py-3 font-semibold">Método</th>
                    <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} className="border-b border-surface-border/50 last:border-0 hover:bg-surface-panel/40">
                      <td className="px-4 py-3 font-semibold text-ink tabular-nums">
                        {fmtMoney(p.amount, p.currency)}
                        {p.profitSplitPct != null && (
                          <span className="ml-1.5 text-[11px] text-ink-faint">split {p.profitSplitPct}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-md border text-[11px] font-medium ${STATUS_TONE[p.status]}`}>
                          {STATUS_LABEL[p.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-ink-muted">{p.account?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-ink-muted tabular-nums">{fmtDate(p.requestedAt)}</td>
                      <td className="px-4 py-3 text-ink-muted tabular-nums">{fmtDate(p.paidAt)}</td>
                      <td className="px-4 py-3 text-ink-muted">{p.method ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1.5">
                          {p.status === 'requested' && (
                            <Button size="sm" variant="secondary" loading={markPaid.isPending} onClick={() => markPaid.mutate(p)}>
                              Marcar cobrado
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setEditTarget(p)}>
                            Editar
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => setDeleteTarget(p)}>
                            ✕
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {(showCreate || editTarget) && (
        <PayoutFormModal
          target={editTarget}
          accounts={accounts ?? []}
          defaultAccountId={accountId || fundedAccounts[0]?.id || ''}
          onClose={() => {
            setShowCreate(false);
            setEditTarget(null);
          }}
        />
      )}

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar payout"
        description={deleteTarget ? `Vas a eliminar el retiro de ${fmtMoney(deleteTarget.amount, deleteTarget.currency)}.` : ''}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="danger" loading={deleteMutation.isPending} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}>
              Sí, eliminar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-ink-muted">Esta acción no se puede deshacer.</p>
      </Modal>
    </AppLayout>
  );
}

function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'win' | 'warn';
}): ReactElement {
  const valueColor = tone === 'win' ? 'text-win-DEFAULT' : tone === 'warn' ? 'text-warn-DEFAULT' : 'text-ink';
  return (
    <div className="rounded-xl border border-surface-border bg-surface-elevated p-4">
      <div className="text-[11px] text-ink-subtle uppercase tracking-wider mb-1.5">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${valueColor}`}>{value}</div>
      {hint && <div className="text-xs text-ink-faint mt-1">{hint}</div>}
    </div>
  );
}

function EmptyState({ onCreate, hasFunded }: { onCreate: () => void; hasFunded: boolean }): ReactElement {
  return (
    <div className="rounded-2xl border-2 border-dashed border-surface-border bg-surface-elevated/30 p-12 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-brand-500/10 border border-brand-500/30 flex items-center justify-center text-brand-500 text-2xl">
        ◈
      </div>
      <h3 className="text-lg font-semibold text-ink mb-1">Aún no registraste payouts</h3>
      <p className="text-sm text-ink-muted mb-6 max-w-md mx-auto">
        {hasFunded
          ? 'Cuando cobres (o pidas) un retiro de tu cuenta fondeada, registralo acá para llevar el ingreso real de tu trading.'
          : 'Los payouts salen de cuentas fondeadas. Cuando pases una cuenta a fondeada y cobres tu primer retiro, registralo acá.'}
      </p>
      <Button variant="primary" onClick={onCreate}>
        + Registrar primer payout
      </Button>
    </div>
  );
}

function PayoutFormModal({
  target,
  accounts,
  defaultAccountId,
  onClose,
}: {
  target: Payout | null;
  accounts: TradingAccount[];
  defaultAccountId: string;
  onClose: () => void;
}): ReactElement {
  const qc = useQueryClient();
  const isEdit = !!target;
  const toDateInput = (iso: string | null | undefined): string => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

  const [accountId, setAccountId] = useState(target?.accountId ?? defaultAccountId);
  const [amount, setAmount] = useState(target ? String(target.amount) : '');
  const [status, setStatus] = useState<Payout['status']>(target?.status ?? 'requested');
  const [method, setMethod] = useState(target?.method ?? 'rise');
  const [profitSplit, setProfitSplit] = useState(target?.profitSplitPct != null ? String(target.profitSplitPct) : '');
  const [requestedAt, setRequestedAt] = useState(toDateInput(target?.requestedAt) || new Date().toISOString().slice(0, 10));
  const [paidAt, setPaidAt] = useState(toDateInput(target?.paidAt));
  const [notes, setNotes] = useState(target?.notes ?? '');
  const [err, setErr] = useState<string | null>(null);

  const accountOptions = accounts.map((a) => ({ value: a.id, label: a.name }));

  const save = useMutation({
    mutationFn: async () => {
      const amountNum = Number(amount);
      if (!accountId) throw new Error('Elegí una cuenta.');
      if (!Number.isFinite(amountNum) || amountNum <= 0) throw new Error('Monto inválido.');
      const body: Record<string, unknown> = {
        amount: amountNum,
        status,
        method,
        profitSplitPct: profitSplit ? Number(profitSplit) : null,
        requestedAt: new Date(requestedAt).toISOString(),
        paidAt: status === 'paid' ? new Date(paidAt || requestedAt).toISOString() : paidAt ? new Date(paidAt).toISOString() : null,
        notes: notes || null,
      };
      if (isEdit) {
        return api(`/payouts/${target!.id}`, { method: 'PATCH', body });
      }
      return api('/payouts', { method: 'POST', body: { ...body, accountId } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts'] });
      qc.invalidateQueries({ queryKey: ['payouts-summary'] });
      onClose();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'No se pudo guardar'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Editar payout' : 'Registrar payout'}
      description="Un retiro cobrado o pedido sobre una cuenta fondeada."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => { setErr(null); save.mutate(); }}>
            {isEdit ? 'Guardar' : 'Registrar'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {!isEdit && (
          <Select label="Cuenta" options={accountOptions} value={accountId} onChange={setAccountId} placeholder="Elegí una cuenta" />
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Monto" type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="600" />
          <Select label="Estado" options={STATUS_OPTIONS} value={status} onChange={(v) => setStatus(v as Payout['status'])} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Método" options={METHOD_OPTIONS} value={method} onChange={setMethod} />
          <Input label="Split % (opcional)" type="number" inputMode="decimal" value={profitSplit} onChange={(e) => setProfitSplit(e.target.value)} placeholder="80" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Fecha de pedido" type="date" value={requestedAt} onChange={(e) => setRequestedAt(e.target.value)} />
          <Input label="Fecha de cobro" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} hint={status === 'paid' && !paidAt ? 'Se usa la de pedido si la dejás vacía' : undefined} />
        </div>
        <Input label="Notas (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej. primer retiro funded" />
        {err && <p className="text-sm text-loss-DEFAULT">{err}</p>}
      </div>
    </Modal>
  );
}
